// Minimal, self-contained MD5 implementation (RFC 1321) — Web Crypto's
// SubtleCrypto intentionally does not support MD5, so SHA-1/256 use the
// native API but MD5 has to be hand-rolled.
const rotateLeft = (x, c) => (x << c) | (x >>> (32 - c))

const addUnsigned = (x, y) => {
  const x4 = x & 0x80000000
  const y4 = y & 0x80000000
  const x8 = x & 0x40000000
  const y8 = y & 0x40000000
  const result = (x & 0x3fffffff) + (y & 0x3fffffff)
  if (x8 & y8) return result ^ 0x80000000 ^ x4 ^ y4
  if (x8 | y8) {
    if (result & 0x40000000) return result ^ 0xc0000000 ^ x4 ^ y4
    return result ^ 0x40000000 ^ x4 ^ y4
  }
  return result ^ x4 ^ y4
}

const F = (x, y, z) => (x & y) | (~x & z)
const G = (x, y, z) => (x & z) | (y & ~z)
const H = (x, y, z) => x ^ y ^ z
const I = (x, y, z) => y ^ (x | ~z)

const FF = (a, b, c, d, x, s, ac) => {
  a = addUnsigned(a, addUnsigned(addUnsigned(F(b, c, d), x), ac))
  return addUnsigned(rotateLeft(a, s), b)
}
const GG = (a, b, c, d, x, s, ac) => {
  a = addUnsigned(a, addUnsigned(addUnsigned(G(b, c, d), x), ac))
  return addUnsigned(rotateLeft(a, s), b)
}
const HH = (a, b, c, d, x, s, ac) => {
  a = addUnsigned(a, addUnsigned(addUnsigned(H(b, c, d), x), ac))
  return addUnsigned(rotateLeft(a, s), b)
}
const II = (a, b, c, d, x, s, ac) => {
  a = addUnsigned(a, addUnsigned(addUnsigned(I(b, c, d), x), ac))
  return addUnsigned(rotateLeft(a, s), b)
}

const convertToWordArray = (str) => {
  const bytes = []
  for (let i = 0; i < str.length; i++) bytes.push(str.charCodeAt(i) & 0xff)
  const messageLength = bytes.length
  bytes.push(0x80)
  while (bytes.length % 64 !== 56) bytes.push(0)
  const bitLength = messageLength * 8
  bytes.push(bitLength & 0xff, (bitLength >>> 8) & 0xff, (bitLength >>> 16) & 0xff, (bitLength >>> 24) & 0xff, 0, 0, 0, 0)

  const words = []
  for (let i = 0; i < bytes.length; i += 4) {
    words.push(bytes[i] | (bytes[i + 1] << 8) | (bytes[i + 2] << 16) | (bytes[i + 3] << 24))
  }
  return words
}

const toHex = (num) => {
  let result = ''
  for (let i = 0; i <= 3; i++) {
    const byte = (num >>> (i * 8)) & 255
    result += byte.toString(16).padStart(2, '0')
  }
  return result
}

export const md5 = (input) => {
  const utf8 = unescape(encodeURIComponent(input))
  const x = convertToWordArray(utf8)
  let a = 0x67452301
  let b = 0xefcdab89
  let c = 0x98badcfe
  let d = 0x10325476

  for (let i = 0; i < x.length; i += 16) {
    const [aa, bb, cc, dd] = [a, b, c, d]

    a = FF(a, b, c, d, x[i + 0], 7, 0xd76aa478); d = FF(d, a, b, c, x[i + 1], 12, 0xe8c7b756)
    c = FF(c, d, a, b, x[i + 2], 17, 0x242070db); b = FF(b, c, d, a, x[i + 3], 22, 0xc1bdceee)
    a = FF(a, b, c, d, x[i + 4], 7, 0xf57c0faf); d = FF(d, a, b, c, x[i + 5], 12, 0x4787c62a)
    c = FF(c, d, a, b, x[i + 6], 17, 0xa8304613); b = FF(b, c, d, a, x[i + 7], 22, 0xfd469501)
    a = FF(a, b, c, d, x[i + 8], 7, 0x698098d8); d = FF(d, a, b, c, x[i + 9], 12, 0x8b44f7af)
    c = FF(c, d, a, b, x[i + 10], 17, 0xffff5bb1); b = FF(b, c, d, a, x[i + 11], 22, 0x895cd7be)
    a = FF(a, b, c, d, x[i + 12], 7, 0x6b901122); d = FF(d, a, b, c, x[i + 13], 12, 0xfd987193)
    c = FF(c, d, a, b, x[i + 14], 17, 0xa679438e); b = FF(b, c, d, a, x[i + 15], 22, 0x49b40821)

    a = GG(a, b, c, d, x[i + 1], 5, 0xf61e2562); d = GG(d, a, b, c, x[i + 6], 9, 0xc040b340)
    c = GG(c, d, a, b, x[i + 11], 14, 0x265e5a51); b = GG(b, c, d, a, x[i + 0], 20, 0xe9b6c7aa)
    a = GG(a, b, c, d, x[i + 5], 5, 0xd62f105d); d = GG(d, a, b, c, x[i + 10], 9, 0x02441453)
    c = GG(c, d, a, b, x[i + 15], 14, 0xd8a1e681); b = GG(b, c, d, a, x[i + 4], 20, 0xe7d3fbc8)
    a = GG(a, b, c, d, x[i + 9], 5, 0x21e1cde6); d = GG(d, a, b, c, x[i + 14], 9, 0xc33707d6)
    c = GG(c, d, a, b, x[i + 3], 14, 0xf4d50d87); b = GG(b, c, d, a, x[i + 8], 20, 0x455a14ed)
    a = GG(a, b, c, d, x[i + 13], 5, 0xa9e3e905); d = GG(d, a, b, c, x[i + 2], 9, 0xfcefa3f8)
    c = GG(c, d, a, b, x[i + 7], 14, 0x676f02d9); b = GG(b, c, d, a, x[i + 12], 20, 0x8d2a4c8a)

    a = HH(a, b, c, d, x[i + 5], 4, 0xfffa3942); d = HH(d, a, b, c, x[i + 8], 11, 0x8771f681)
    c = HH(c, d, a, b, x[i + 11], 16, 0x6d9d6122); b = HH(b, c, d, a, x[i + 14], 23, 0xfde5380c)
    a = HH(a, b, c, d, x[i + 1], 4, 0xa4beea44); d = HH(d, a, b, c, x[i + 4], 11, 0x4bdecfa9)
    c = HH(c, d, a, b, x[i + 7], 16, 0xf6bb4b60); b = HH(b, c, d, a, x[i + 10], 23, 0xbebfbc70)
    a = HH(a, b, c, d, x[i + 13], 4, 0x289b7ec6); d = HH(d, a, b, c, x[i + 0], 11, 0xeaa127fa)
    c = HH(c, d, a, b, x[i + 3], 16, 0xd4ef3085); b = HH(b, c, d, a, x[i + 6], 23, 0x04881d05)
    a = HH(a, b, c, d, x[i + 9], 4, 0xd9d4d039); d = HH(d, a, b, c, x[i + 12], 11, 0xe6db99e5)
    c = HH(c, d, a, b, x[i + 15], 16, 0x1fa27cf8); b = HH(b, c, d, a, x[i + 2], 23, 0xc4ac5665)

    a = II(a, b, c, d, x[i + 0], 6, 0xf4292244); d = II(d, a, b, c, x[i + 7], 10, 0x432aff97)
    c = II(c, d, a, b, x[i + 14], 15, 0xab9423a7); b = II(b, c, d, a, x[i + 5], 21, 0xfc93a039)
    a = II(a, b, c, d, x[i + 12], 6, 0x655b59c3); d = II(d, a, b, c, x[i + 3], 10, 0x8f0ccc92)
    c = II(c, d, a, b, x[i + 10], 15, 0xffeff47d); b = II(b, c, d, a, x[i + 1], 21, 0x85845dd1)
    a = II(a, b, c, d, x[i + 8], 6, 0x6fa87e4f); d = II(d, a, b, c, x[i + 15], 10, 0xfe2ce6e0)
    c = II(c, d, a, b, x[i + 6], 15, 0xa3014314); b = II(b, c, d, a, x[i + 13], 21, 0x4e0811a1)
    a = II(a, b, c, d, x[i + 4], 6, 0xf7537e82); d = II(d, a, b, c, x[i + 11], 10, 0xbd3af235)
    c = II(c, d, a, b, x[i + 2], 15, 0x2ad7d2bb); b = II(b, c, d, a, x[i + 9], 21, 0xeb86d391)

    a = addUnsigned(a, aa)
    b = addUnsigned(b, bb)
    c = addUnsigned(c, cc)
    d = addUnsigned(d, dd)
  }

  return toHex(a) + toHex(b) + toHex(c) + toHex(d)
}
